/**
 * PipelineRunList Component
 *
 * Displays list of pipeline runs for a site with status and actions.
 *
 * Part of Story 54: Automated AEO Pipeline with Pilot Mode and Impact Propagation
 */

import { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Loader2, Plus, Play, Eye, RefreshCw, Trash2, Search } from 'lucide-react';
import {
  listPipelineRuns,
  createPipelineRun,
  startPilot,
  deletePipelineRun,
  searchPipelineCategories,
  PipelineRun,
  PipelineState,
  PipelineConfig,
  ProcessingMode,
  CategoryOption,
} from '@/services/pipelineService';
import { useAuth } from '@/context/auth-context';
import { formatDistanceToNow } from 'date-fns';

interface PipelineRunListProps {
  siteId: string;
  onSelectRun: (runId: string) => void;
  selectedRunId?: string;
}

const stateColors: Record<PipelineState, string> = {
  CONFIGURING: 'bg-gray-500',
  PILOT_RUNNING: 'bg-blue-500',
  PILOT_REVIEW: 'bg-yellow-500',
  PILOT_REJECTED: 'bg-red-500',
  FULL_RUNNING: 'bg-blue-600',
  FULL_PAUSED: 'bg-orange-500',
  IMPACT_ANALYSIS: 'bg-purple-500',
  IMPACT_REVIEW: 'bg-yellow-600',
  PROPAGATION_RUNNING: 'bg-indigo-500',
  COMPLETED: 'bg-green-500',
  FAILED: 'bg-red-600',
};

const stateLabels: Record<PipelineState, string> = {
  CONFIGURING: 'Configuring',
  PILOT_RUNNING: 'Pilot Running',
  PILOT_REVIEW: 'Awaiting Pilot Review',
  PILOT_REJECTED: 'Pilot Rejected',
  FULL_RUNNING: 'Full Generation',
  FULL_PAUSED: 'Paused',
  IMPACT_ANALYSIS: 'Analyzing Impact',
  IMPACT_REVIEW: 'Awaiting Impact Review',
  PROPAGATION_RUNNING: 'Propagating',
  COMPLETED: 'Completed',
  FAILED: 'Failed',
};

export function PipelineRunList({
  siteId,
  onSelectRun,
  selectedRunId,
}: PipelineRunListProps) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  const [runs, setRuns] = useState<PipelineRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newConfig, setNewConfig] = useState<Partial<PipelineConfig>>({
    pilot_category: '',
    pilot_product_count: 5,
    language: 'es',
    max_iterations_per_product: 3,
    processing_mode: 'auto',
  });

  // Category search state
  const [categorySearch, setCategorySearch] = useState('');
  const [categoryOptions, setCategoryOptions] = useState<CategoryOption[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const categoryInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const loadRuns = async () => {
    setLoading(true);
    try {
      const result = await listPipelineRuns(siteId, { limit: 20 });
      setRuns(result.runs);
    } catch (error) {
      console.error('Failed to load pipeline runs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (siteId) {
      loadRuns();
    }
  }, [siteId]);

  // Search categories with debounce
  useEffect(() => {
    if (!siteId || !showCreateForm) return;

    const searchCategories = async () => {
      setLoadingCategories(true);
      try {
        const result = await searchPipelineCategories(siteId, categorySearch);
        if (result.success) {
          setCategoryOptions(result.categories);
        }
      } catch (error) {
        console.error('Failed to search categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    const debounce = setTimeout(searchCategories, 300);
    return () => clearTimeout(debounce);
  }, [siteId, categorySearch, showCreateForm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        categoryInputRef.current &&
        !categoryInputRef.current.contains(event.target as Node)
      ) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDelete = async (runId: string) => {
    setDeleting(runId);
    try {
      await deletePipelineRun(runId);
      await loadRuns();
      if (selectedRunId === runId) {
        onSelectRun('');
      }
    } catch (error) {
      console.error('Failed to delete pipeline run:', error);
    } finally {
      setDeleting(null);
    }
  };

  const selectCategory = (category: CategoryOption) => {
    setNewConfig({ ...newConfig, pilot_category: category.name });
    setCategorySearch(category.name);
    setShowCategoryDropdown(false);
  };

  const handleCreate = async () => {
    if (!newConfig.pilot_category) return;

    setCreating(true);
    try {
      const result = await createPipelineRun(siteId, newConfig as PipelineConfig);
      if (result.success && result.run) {
        // Start pilot automatically
        await startPilot(result.run.id);
        setShowCreateForm(false);
        setNewConfig({ pilot_category: '', pilot_product_count: 5, language: 'es', max_iterations_per_product: 3, processing_mode: 'auto' });
        setCategorySearch('');
        setCategoryOptions([]);
        await loadRuns();
        onSelectRun(result.run.id);
      }
    } catch (error) {
      console.error('Failed to create pipeline run:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleCloseForm = () => {
    setShowCreateForm(false);
    setCategorySearch('');
    setCategoryOptions([]);
    setNewConfig({ pilot_category: '', pilot_product_count: 5, language: 'es', max_iterations_per_product: 3, processing_mode: 'auto' });
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-base font-medium">Pipeline Runs</CardTitle>
          <CardDescription>AEO generation pipelines for this site</CardDescription>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={loadRuns}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          <Button size="sm" onClick={() => setShowCreateForm(!showCreateForm)}>
            <Plus className="h-4 w-4 mr-1" />
            New Pipeline
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {showCreateForm && (
          <div className="mb-4 p-4 border rounded-lg bg-muted/50">
            <h4 className="font-medium mb-3">Create New Pipeline</h4>
            <div className="grid gap-3">
              <div className="relative">
                <label className="text-sm text-muted-foreground">Category</label>
                <div className="relative mt-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    ref={categoryInputRef}
                    type="text"
                    className="w-full pl-9 pr-3 py-2 border rounded-md text-sm bg-background text-foreground"
                    placeholder="Search categories..."
                    value={categorySearch}
                    onChange={(e) => {
                      setCategorySearch(e.target.value);
                      setNewConfig({ ...newConfig, pilot_category: e.target.value });
                      setShowCategoryDropdown(true);
                    }}
                    onFocus={() => setShowCategoryDropdown(true)}
                  />
                  {loadingCategories && (
                    <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
                  )}
                </div>
                {showCategoryDropdown && categoryOptions.length > 0 && (
                  <div
                    ref={dropdownRef}
                    className="absolute z-50 w-full mt-1 bg-background border rounded-md shadow-lg max-h-80 overflow-auto"
                    style={{ minWidth: '400px' }}
                  >
                    {categoryOptions.map((cat) => {
                      // Extract the last category name and parent path
                      const parts = cat.name.split(' > ');
                      const categoryName = parts[parts.length - 1];
                      const parentPath = parts.slice(0, -1).join(' > ');

                      return (
                        <button
                          key={cat.name}
                          type="button"
                          className="w-full px-3 py-2 text-left hover:bg-muted border-b last:border-b-0"
                          onClick={() => selectCategory(cat)}
                          title={cat.name}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="font-medium text-sm">{categoryName}</div>
                              {parentPath && (
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  {parentPath}
                                </div>
                              )}
                            </div>
                            <span className="text-xs text-muted-foreground whitespace-nowrap bg-muted px-2 py-0.5 rounded">
                              {cat.productCount} products
                            </span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  Search and select a category from your feed
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-sm text-muted-foreground">Pilot Products</label>
                  <input
                    type="number"
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-background text-foreground"
                    min={1}
                    max={50}
                    value={newConfig.pilot_product_count || 5}
                    onChange={(e) =>
                      setNewConfig({
                        ...newConfig,
                        pilot_product_count: parseInt(e.target.value),
                      })
                    }
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Sample size for pilot review
                  </p>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Language</label>
                  <Select
                    value={newConfig.language}
                    onValueChange={(value) =>
                      setNewConfig({ ...newConfig, language: value })
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="es">Spanish</SelectItem>
                      <SelectItem value="en">English</SelectItem>
                      <SelectItem value="ca">Catalan</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Max Iterations</label>
                  <input
                    type="number"
                    className="w-full mt-1 px-3 py-2 border rounded-md text-sm bg-background text-foreground"
                    min={1}
                    max={10}
                    value={newConfig.max_iterations_per_product || 3}
                    onChange={(e) =>
                      setNewConfig({
                        ...newConfig,
                        max_iterations_per_product: parseInt(e.target.value),
                      })
                    }
                  />
                </div>
              </div>
              {/* Processing Mode - Super Admin only */}
              {isSuperAdmin && (
                <div className="mt-3">
                  <label className="text-sm text-muted-foreground">Processing Mode</label>
                  <Select
                    value={newConfig.processing_mode || 'auto'}
                    onValueChange={(value: ProcessingMode) =>
                      setNewConfig({ ...newConfig, processing_mode: value })
                    }
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Auto (batch for &gt;20 products)</SelectItem>
                      <SelectItem value="batch">Batch (50% cheaper, up to 24h)</SelectItem>
                      <SelectItem value="realtime">Realtime (immediate)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {newConfig.processing_mode === 'auto' && 'Uses batch API for runs with >20 products (50% cost savings)'}
                    {newConfig.processing_mode === 'batch' && 'Always uses OpenAI Batch API (50% cheaper, may take up to 24h)'}
                    {newConfig.processing_mode === 'realtime' && 'Always uses real-time API (immediate processing)'}
                  </p>
                </div>
              )}
              <div className="flex justify-end gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCloseForm}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleCreate}
                  disabled={creating || !newConfig.pilot_category}
                >
                  {creating ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : (
                    <Play className="h-4 w-4 mr-1" />
                  )}
                  Start Pipeline
                </Button>
              </div>
            </div>
          </div>
        )}

        {runs.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <p>No pipeline runs yet.</p>
            <p className="text-sm mt-1">Create a new pipeline to get started.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {runs.map((run) => (
              <div
                key={run.id}
                className={`p-3 border rounded-lg cursor-pointer transition-colors hover:bg-muted/50 ${
                  selectedRunId === run.id ? 'border-primary bg-muted/50' : ''
                }`}
                onClick={() => onSelectRun(run.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge className={`${stateColors[run.state]} text-white text-xs`}>
                      {stateLabels[run.state]}
                    </Badge>
                    <span className="text-sm font-medium">
                      {run.config?.pilot_category || 'Unknown Category'}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onSelectRun(run.id); }}>
                      <Eye className="h-4 w-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={(e) => e.stopPropagation()}
                          disabled={deleting === run.id}
                        >
                          {deleting === run.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Trash2 className="h-4 w-4" />
                          )}
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent onClick={(e) => e.stopPropagation()}>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Pipeline Run?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete the pipeline run for "{run.config?.pilot_category || 'Unknown Category'}" and all associated product statuses. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(run.id)}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
                <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                  <span>
                    Created {formatDistanceToNow(new Date(run.created_at), { addSuffix: true })}
                  </span>
                  {run.metrics && (
                    <>
                      <span>{run.metrics.processed_products || 0} processed</span>
                      <span>{run.metrics.passed_products || 0} passed</span>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
